import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { axInst } from "../../../Components/Login/Service";
import { IoIosArrowForward, IoIosArrowDown } from "react-icons/io";
import { Loader } from "rsuite";
import { BsBoxArrowInUpRight } from "react-icons/bs";
import MsVahanModal from "../../../Components/Modals/MsVahanModal";
import MsVahanDistrictTiv from "./MsVahanDistrictTiv";

// Product line mapping
const productLineMap = {
  HCV: "HCV Trucks",
  ILMCV: "ILMCV Trucks",
  SCV: "SCV & PU",
  "CV Pass": "CV Passenger",
};

// Extracted API calls to separate functions
const msVahanTivApi = async (body) => {
  try {
    const res = await axInst.post("dealer-review/tiv_tml_vaahan", body);
    return res.data;
  } catch (error) {
    console.error("Error fetching MS Vahan data:", error);
    throw error; // Re-throw error to be caught by React Query
  }
};

const districtsApi = async (filterBody) => {
  // Added filterBody parameter
  try {
    const res = await axInst.post(
      "dealer-review/dealer_review_filter",
      filterBody
    ); // Pass filterBody
    return res.data.DISTRICT.buckets || [];
  } catch (error) {
    console.error("Error fetching district list:", error);
    throw error; // Re-throw error to be caught by React Query
  }
};

const districtsDataApi = async (body) => {
  try {
    const res = await axInst.post(
      "dealer-review/tiv_tml_vaahan_district_wise",
      body
    );
    return res.data || [];
  } catch (error) {
    console.error("Error fetching district data:", error);
    throw error; // Re-throw error to be caught by React Query
  }
};

// Multi-select dropdown component
const MultiSelectDropdown = ({
  options,
  selectedValues,
  onApply,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [draftSelectedValues, setDraftSelectedValues] =
    useState(selectedValues);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (
      !isOpen &&
      JSON.stringify(draftSelectedValues) !== JSON.stringify(selectedValues)
    ) {
      setDraftSelectedValues(selectedValues);
    }
  }, [selectedValues, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setDraftSelectedValues(selectedValues);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedValues]);

  const filteredOptions = useMemo(() => {
    if (!searchText.trim()) return options;
    return options.filter((option) =>
      option.key.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [options, searchText]);

  const toggleOption = (option) => {
    setDraftSelectedValues((prev) => {
      if (prev.includes(option.key)) {
        return prev.filter((value) => value !== option.key);
      } else {
        return [...prev, option.key];
      }
    });
  };

  const clearAll = () => {
    setDraftSelectedValues([]);
  };

  const handleApplyClick = () => {
    if (
      JSON.stringify(draftSelectedValues) !== JSON.stringify(selectedValues)
    ) {
      onApply(draftSelectedValues);
    }
    setIsOpen(false);
  };

  return (
    <div className="relative md:w-[11rem] w-[10rem]" ref={dropdownRef}>
      <div
        className={`flex items-center justify-between p-[5px] border border-gray-300 rounded-md cursor-pointer bg-white ${
          disabled ? "opacity-50" : ""
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="text-sm text-gray-500">
          {selectedValues.length > 0
            ? `${selectedValues.length} selected`
            : "Select Districts"}
        </div>
        <div className="flex items-center">
          <span className="text-xs text-gray-500 mr-2">
            {selectedValues.length}/{options.length}
          </span>
          <IoIosArrowDown
            className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          {/* Search Input */}
          <div className="sticky top-0 bg-white p-2 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search..."
              className="w-full p-1 border border-gray-300 rounded-md text-sm"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="flex justify-end px-2 pt-1">
            <button
              className="text-blue-600 hover:text-blue-800 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                clearAll();
              }}
            >
              Clear Selection
            </button>
          </div>
          {/* Options Scrollable Area */}
          <div className="max-h-48 overflow-auto">
            {filteredOptions.map((option) => (
              <div
                key={option.key}
                className={`px-2 py-1 hover:bg-gray-100 cursor-pointer text-sm ${
                  draftSelectedValues.includes(option.key) ? "bg-blue-50" : ""
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleOption(option);
                }}
              >
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={draftSelectedValues.includes(option.key)}
                    onChange={() => toggleOption(option)}
                    className="mr-2"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span>{option.key}</span>
                </div>
              </div>
            ))}
            {filteredOptions.length === 0 && (
              <div className="p-2 text-center text-gray-500">
                No matching districts
              </div>
            )}
          </div>

          {/* Buttons at the bottom */}
          <div className="sticky bottom-0 bg-white  border-t border-gray-200 text-xs flex  items-center gap-2">
            <button
              className="px-3 py-1 bg-[#2A5775] w-full text-white "
              onClick={(e) => {
                e.stopPropagation();
                handleApplyClick();
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const ExpandedRowContent = React.memo(
  ({ data, columns, parentLeafColumnWidths }) => {
    const table = useReactTable({
      data,
      columns,
      getCoreRowModel: getCoreRowModel(),
    });

    if (!data || data.length === 0) {
      return (
        <div className="text-center py-4">No model-specific data available</div>
      );
    }

    return (
      <div className="col-span-full w-full">
        <div className="max-h-36 overflow-y-auto overflow-x-hidden">
          <table className="min-w-full border-collapse text-xs">
            <tbody className="bg-white text-center text-xs text-gray-700">
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={`w-full text-xs border ${
                    row.index % 2 === 0 ? "bg-[#9cbed3]" : "bg-[#9cbed3]"
                  }`}
                >
                  {row.getVisibleCells().map((cell, cellIndex) => (
                    <td
                      key={cell.id}
                      className="border border-gray-300 py-[6px] text-xs"
                      style={{ width: parentLeafColumnWidths[cellIndex] }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
);

const MsVahanTiv2 = ({ show }) => {
  const { data: filterData, submitted } = useSelector(
    (e) => e.filtersDealerReview
  );
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [isApplyingDistrict, setIsApplyingDistrict] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const tableRef = useRef(null);
  const [parentLeafColumnWidths, setParentLeafColumnWidths] = useState([]);
  const [openMsVahanModal, setIsOpenMsVahanModal] = useState(false);
  const handleOpenMsVahanModal = () => {
    setIsOpenMsVahanModal(true);
  };
  const handleCloseMsVahanModal = () => {
    setIsOpenMsVahanModal(false);
  };

  const {
    data: districtListData = [],
    isLoading: isDistrictListLoading,
    error: districtListError,
  } = useQuery({
    queryKey: ["districtList", JSON.stringify(filterData)],
    queryFn: () => districtsApi(filterData),
    staleTime: 0,
    cacheTime: 0,
  });
  useEffect(() => {
    if (districtListData.length > 0 && selectedDistricts.length > 0) {
      const validSelections = selectedDistricts.filter((s) =>
        districtListData.some((d) => d.key === s)
      );
      if (validSelections.length !== selectedDistricts.length) {
        setSelectedDistricts(validSelections);
      }
    } else if (districtListData.length === 0 && selectedDistricts.length > 0) {
      setSelectedDistricts([]);
    }
  }, [districtListData, selectedDistricts]);

  // Prepare modified filter data with selected districts
  const modifiedFilterData = useMemo(() => {
    if (!filterData) return null;

    let baseFilter = { ...filterData };
    // Only include District filter if selectedDistricts is not empty
    if (selectedDistricts.length > 0) {
      baseFilter.District = selectedDistricts;
    }
    return baseFilter;
  }, [filterData, selectedDistricts]);

  // Main data fetching for MS Vahan
  const {
    data: msVahanData = [],
    isLoading: isMsVahanLoading,
    error: msVahanError,
  } = useQuery({
    queryKey: ["msVahan", submitted, JSON.stringify(modifiedFilterData)],
    queryFn: () => msVahanTivApi(modifiedFilterData),
    enabled:
      submitted !== undefined && !isApplyingDistrict && !isDistrictListLoading,
    staleTime: 0,
    cacheTime: 0,
  });

  // Handle row expansion
  const handleRowExpand = useCallback((rowId, lob) => {
    setExpandedRows((prev) => {
      if (prev[rowId] === lob) {
        const newState = { ...prev };
        delete newState[rowId];
        return newState;
      }
      return { ...prev, [rowId]: lob };
    });
  }, []);

  const tableData = useMemo(() => {
    if (
      !msVahanData ||
      !Array.isArray(msVahanData) ||
      msVahanData?.length === 0
    )
      return [];

    return msVahanData.map((item) => {
      const rowData = { LOB: item.LOB };

      // Process month_performance dynamically
      const monthPerformance = item.month_performance || {};
      Object.keys(monthPerformance).forEach((key) => {
        if (key === "variance") {
          rowData.month_variance = monthPerformance.variance; // Rename variance key
        } else {
          rowData[key] = monthPerformance[key]; // Add dynamic month keys
        }
      });

      // Process ytd_performance dynamically
      const ytdPerformance = item.ytd_performance || {};
      Object.keys(ytdPerformance).forEach((key) => {
        if (key === "variance") {
          rowData.ytd_variance = ytdPerformance.variance; // Rename variance key
        } else {
          rowData[key] = ytdPerformance[key]; // Add dynamic YTD keys
        }
      });

      // Process quarterly_performance directly as it has no conflicting 'variance' key
      const quarterlyPerformance = item.quarterly_performance || {};
      Object.keys(quarterlyPerformance).forEach((key) => {
        rowData[key] = quarterlyPerformance[key]; // Add dynamic quarterly keys
      });

      return rowData;
    });
  }, [msVahanData]);

  // const totals = useMemo(() => {
  //   if (!tableData?.length) return null;

  //   const allKeys = Array.from(
  //     new Set(tableData.flatMap((row) => Object.keys(row)))
  //   );

  //   const totalRow = { LOB: "Total" };

  //   allKeys.forEach((key) => {
  //     if (key === "LOB") return;

  //     let values = tableData.map((row) => {
  //       const raw = row[key];
  //       if (raw == null) return 0;

  //       const numeric = parseFloat(String(raw).replace("%", "").trim());
  //       return isNaN(numeric) ? 0 : numeric;
  //     });

  //     const sum = values.reduce((a, b) => a + b, 0);
  //     const hasPercentage = tableData.some(
  //       (row) => typeof row[key] === "string" && String(row[key]).includes("%")
  //     );

  //     if (hasPercentage) {
  //       const avg = values.length ? sum / values.length : 0;
  //       totalRow[key] = `${avg.toFixed(2)}%`;
  //     } else {
  //       totalRow[key] = sum.toFixed(2);
  //     }
  //   });

  //   return totalRow;
  // }, [tableData]);

  const totals = useMemo(() => {
    if (!tableData?.length) return null;

    const allKeys = Array.from(
      new Set(tableData.flatMap((row) => Object.keys(row)))
    );

    const totalRow = { LOB: "Total" };

    allKeys.forEach((key) => {
      if (key === "LOB") return;

      const values = tableData.map((row) => {
        const raw = row[key];
        if (raw == null) return 0;

        const numeric = parseFloat(String(raw).replace("%", "").trim());
        return isNaN(numeric) ? 0 : numeric;
      });

      const sum = values.reduce((a, b) => a + b, 0);

      const hasPercentage = tableData.some(
        (row) => typeof row[key] === "string" && String(row[key]).includes("%")
      );

      if (hasPercentage) {
        const avg = values.length ? sum / values.length : 0;
        totalRow[key] = `${Math.round(avg)}%`;
      } else {
        totalRow[key] = Math.round(sum);
      }
    });

    return totalRow;
  }, [tableData]);

  // Generate table columns only when msVahanData changes
  const columns = useMemo(() => {
    if (
      !msVahanData ||
      !Array.isArray(msVahanData) ||
      msVahanData?.length === 0
    )
      return [];

    const sample = msVahanData[0];
    if (!sample) return [];

    const sampleMonthPerf = sample.month_performance || {};
    const dynamicMonthKeys = Object.keys(sampleMonthPerf)
      .filter((key) => key !== "variance")
      .sort();
    const currentMonthKey =
      dynamicMonthKeys.length > 1 ? dynamicMonthKeys[1] : null;
    const prevMonthKey =
      dynamicMonthKeys.length > 0 ? dynamicMonthKeys[0] : null;

    const generateGroup = (
      title,
      obj,
      keyMap = {},
      currentDataKey = null,
      previousDataKey = null
    ) => {
      if (!obj) return { header: title, columns: [] };

      const originalKeys = Object.keys(obj);

      // Detect YTD keys for coloring logic
      const ytdKeys = originalKeys.filter((k) => k !== "variance"); // keep original order
      const firstYtdKey = ytdKeys[0];
      const secondYtdKey = ytdKeys[1]; // now this is truly the 2nd column

      return {
        header: title,
        columns: originalKeys.map((originalKey) => ({
          header: originalKey,
          accessorKey: keyMap[originalKey] || originalKey,
          id: `${title}-${originalKey}`,
          cell: (info) => {
            const value = info.getValue();
            const row = info.row.original;

            // ---------- Month Performance logic ----------
            if (
              originalKey.toLowerCase().includes("variance") &&
              typeof value === "number"
            ) {
              if (
                title === "Month Performance" &&
                currentDataKey &&
                previousDataKey
              ) {
                const currentMonthValue = row[currentDataKey] ?? 0;
                const previousMonthValue = row[previousDataKey] ?? 0;
                if (currentMonthValue === 0 && previousMonthValue > 0)
                  return "N/A*";
              }
              return `${value.toFixed(2)}%`;
            }

            if (
              title === "Month Performance" &&
              currentDataKey &&
              originalKey === currentDataKey
            ) {
              const currentMonthValue = row[currentDataKey] ?? 0;
              const previousMonthValue = row[previousDataKey] ?? 0;
              const color =
                currentMonthValue > previousMonthValue
                  ? "text-green-600"
                  : "text-red-600";
              return <span className={color}>{value ?? "-"}</span>;
            }

            // ---------- YTD Performance coloring (2nd column) ----------
            if (title === "Ytd Performance" && originalKey === secondYtdKey) {
              const prevValue = row[firstYtdKey] ?? 0;
              const color =
                value > prevValue ? "text-green-600" : "text-red-600";
              return <span className={color}>{value ?? "-"}</span>;
            }

            // ---------- Default ----------
            return value ?? "-";
          },
        })),
      };
    };

    return [
      {
        header: "Lob",
        accessorKey: "LOB",
        cell: (info) => info.row.original.LOB ?? "-",
      },
      generateGroup(
        "Month Performance",
        sample.month_performance || {},
        {
          variance: "month_variance",
        },
        currentMonthKey, // Pass dynamic keys for current month
        prevMonthKey // Pass dynamic keys for previous month
      ),
      generateGroup("Ytd Performance", sample.ytd_performance || {}, {
        variance: "ytd_variance",
      }),
      generateGroup(
        "Quarterly Performance",
        sample.quarterly_performance || {},
        {}
      ),
    ];
  }, [msVahanData]);

  const expandedColumns = useMemo(() => {
    if (!msVahanData || !Array.isArray(msVahanData) || msVahanData.length === 0)
      return [];

    const sample = msVahanData[0];
    if (!sample) return [];

    // Dynamically get month keys for N/A logic (for expanded rows too)
    const sampleMonthPerf = sample.month_performance || {};
    const monthKeys = Object.keys(sampleMonthPerf).filter(
      (k) => k !== "variance"
    );
    const currentMonthKey = monthKeys.length > 1 ? monthKeys[1] : null;
    const prevMonthKey = monthKeys.length > 0 ? monthKeys[0] : null;

    // Dynamically get YTD keys for coloring
    const sampleYtdPerf = sample.ytd_performance || {};
    const ytdKeys = Object.keys(sampleYtdPerf).filter((k) => k !== "variance");
    const firstYtdKey = ytdKeys[0];
    const secondYtdKey = ytdKeys[1];

    const generateGroup = (
      title,
      obj,
      varianceAccessorKey,
      currentDataKey = null,
      previousDataKey = null
    ) => {
      if (!obj) return []; // Return empty array if no object

      const originalKeys = Object.keys(obj);

      return originalKeys.map((originalKey) => ({
        header: originalKey,
        accessorKey:
          originalKey === "variance" ? varianceAccessorKey : originalKey,
        id: `expanded-${title}-${originalKey}`,
        cell: (info) => {
          const value = info.getValue();
          const row = info.row.original;

          // ---------- Month Performance ----------
          if (title === "Month Performance") {
            if (
              originalKey.toLowerCase().includes("variance") &&
              typeof value === "number"
            ) {
              if (currentDataKey && previousDataKey) {
                const currentMonthValue = row[currentDataKey] ?? 0;
                const previousMonthValue = row[previousDataKey] ?? 0;
                if (currentMonthValue === 0 && previousMonthValue > 0)
                  return "N/A*";
              }
              return `${value.toFixed(2)}%`;
            }

            if (currentDataKey && originalKey === currentDataKey) {
              const currentMonthValue = row[currentDataKey] ?? 0;
              const previousMonthValue = row[previousDataKey] ?? 0;
              const color =
                currentMonthValue > previousMonthValue
                  ? "text-green-600"
                  : "text-red-600";
              return <span className={color}>{value ?? "-"}</span>;
            }
          }

          // ---------- YTD Performance ----------
          if (title === "Ytd Performance" && originalKey === secondYtdKey) {
            const prevValue = row[firstYtdKey] ?? 0;
            const color = value > prevValue ? "text-green-600" : "text-red-600";
            return <span className={color}>{value ?? "-"}</span>;
          }

          // ---------- Variance formatting ----------
          if (
            originalKey.toLowerCase().includes("variance") &&
            typeof value === "number"
          ) {
            return `${value.toFixed(2)}%`;
          }

          // Default fallback
          return value ?? "-";
        },
      }));
    };

    return [
      {
        header: "Model",
        accessorKey: "model",
        cell: (info) => info.getValue() ?? "-",
      },
      ...generateGroup(
        "Month Performance",
        sample.month_performance || {},
        "month_variance",
        currentMonthKey,
        prevMonthKey
      ),
      ...generateGroup(
        "Ytd Performance",
        sample.ytd_performance || {},
        "ytd_variance"
      ),
      ...generateGroup(
        "Quarterly Performance",
        sample.quarterly_performance || {},
        null
      ),
    ];
  }, [msVahanData]);

  // Handle district change with debouncing to prevent UI freezes
  const handleApplyDistricts = useCallback((newValues) => {
    setExpandedRows({});
    setIsApplyingDistrict(true);
    setTimeout(() => {
      setSelectedDistricts(newValues);
      setTimeout(() => {
        setIsApplyingDistrict(false);
      }, 100);
    }, 0);
  }, []);

  // Setup table instance with error handling - hooks must be at the top level
  const tableData_with_totals = useMemo(() => {
    if (!tableData?.length) return tableData;
    return totals ? [...tableData, totals] : tableData;
  }, [tableData, totals]);

  // Call useReactTable at component top level (not inside callback)
  const table = useReactTable({
    data: tableData_with_totals,
    columns: columns?.length ? columns : [],
    getCoreRowModel: getCoreRowModel(),
  });
  useEffect(() => {
    if (tableRef.current) {
      const lastHeaderRow = tableRef.current.querySelector(
        "thead tr:last-child"
      );
      if (lastHeaderRow) {
        const headerCells = Array.from(lastHeaderRow.querySelectorAll("th"));
        const widths = headerCells.map((th) => `${th.offsetWidth}px`);
        setParentLeafColumnWidths(widths);
      }
    }
  }, [tableData_with_totals, columns]);

  // Process expanded data for a specific lob/rowId
  const processDistrictData = useCallback((rawData, lob) => {
    if (!rawData || !Array.isArray(rawData) || rawData?.length === 0) return [];

    return rawData.map((item) => {
      const rowData = {
        model: item.DISTRICT || item.Model || item.model || item.id || "N/A",
      };

      // Process month_performance dynamically
      const monthPerformance = item.month_performance || {};
      Object.keys(monthPerformance).forEach((key) => {
        if (key === "variance") {
          rowData.month_variance = monthPerformance.variance; // Rename variance key
        } else {
          rowData[key] = monthPerformance[key]; // Add dynamic month keys
        }
      });

      // Process ytd_performance dynamically
      const ytdPerformance = item.ytd_performance || {};
      Object.keys(ytdPerformance).forEach((key) => {
        if (key === "variance") {
          rowData.ytd_variance = ytdPerformance.variance; // Rename variance key
        } else {
          rowData[key] = ytdPerformance[key]; // Add dynamic YTD keys
        }
      });

      // Process quarterly_performance directly as it has no conflicting 'variance' key
      const quarterlyPerformance = item.quarterly_performance || {};
      Object.keys(quarterlyPerformance).forEach((key) => {
        rowData[key] = quarterlyPerformance[key]; // Add dynamic quarterly keys
      });

      return rowData;
    });
  }, []);

  // Handle errors for main data
  const hasMsVahanError = msVahanError;
  // Handle errors for district list
  const hasDistrictListError = districtListError;

  // Combine loading states for the dropdown
  const isLoadingDropdown = isDistrictListLoading || isApplyingDistrict;

  // Overall loading state (for displaying "Loading data..." message)
  const isLoadingOverall =
    isMsVahanLoading || isDistrictListLoading || isApplyingDistrict;

  // Determine if the table content should be displayed (only if data exists and no major errors)
  const shouldDisplayTableContent =
    !isLoadingOverall &&
    msVahanData?.length > 0 &&
    !hasMsVahanError &&
    !hasDistrictListError;

  return (
    <div className="drilltable md:w-full w-[410px]">
      <div className="flex items-center gap-10 p-2 bg-white rounded-t-md">
        <h2 className="md:text-lg text-[16px] font-bold text-gray-800 ">
          Vahan Ms TIV - District Wise
        </h2>
        {!show && (
          <div className="flex gap-2">
            <MultiSelectDropdown
              options={districtListData}
              selectedValues={selectedDistricts}
              onApply={handleApplyDistricts}
              disabled={isLoadingDropdown}
            />
            <MsVahanModal
              title={"Vahan Ms Tiv - District Wise"}
              isOpen={openMsVahanModal}
              handleIsOpenModal={handleOpenMsVahanModal}
              handleIscloseModal={handleCloseMsVahanModal}
              trigger={
                <div
                  onClick={handleOpenMsVahanModal}
                  className="cursor-pointer bg-[#28526e] hover:bg-[white] hover:text-[#28526e] hover:border hover:border-[#28526e] p-2 rounded-md text-white border border-[#28526e]"
                  title="Click to View Detail Vahan Ms % - District Wise"
                >
                  <BsBoxArrowInUpRight className="blink" />
                </div>
              }
            >
              <MsVahanDistrictTiv />
            </MsVahanModal>
          </div>
        )}
      </div>

      {/* Display general errors */}
      {(hasMsVahanError || hasDistrictListError) && (
        <div className="p-4 text-center">
          <p className="text-red-500">
            Error loading data:{" "}
            {hasMsVahanError?.message ||
              hasDistrictListError?.message ||
              "Unknown error"}
          </p>
        </div>
      )}

      {/* Overall loading state */}
      {isLoadingOverall && !hasMsVahanError && !hasDistrictListError && (
        <div className="p-4 text-center">
          {isApplyingDistrict ? (
            <p className="text-sm">
              Updating data based on district selection...
            </p>
          ) : isMsVahanLoading ? (
            <p>Loading overall Vahan MS data, please wait...</p>
          ) : (
            <p>Loading data, please wait...</p>
          )}
        </div>
      )}

      {/* Messages when no data or specific filters */}
      {!isLoadingOverall &&
        !hasMsVahanError &&
        !hasDistrictListError &&
        msVahanData?.length === 0 &&
        (selectedDistricts.length === 0 ? (
          <div className="p-4 text-center">
            <p>No overall Vahan MS data available based on current filters.</p>
          </div>
        ) : (
          <div className="p-4 text-center">
            <p>
              No data available for the selected districts and filters. Please
              adjust your filters and try again.
            </p>
          </div>
        ))}

      {/* Only render table if we have data and columns */}
      {shouldDisplayTableContent && (
        <div className="border border-gray-300">
          <table
            ref={tableRef}
            className="min-w-full border-collapse text-xs table-fixed"
          >
            <thead className="bg-[#28526e] text-sm text-white">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={`headerGroup-${headerGroup.id}`}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={`header-${header.id}`}
                      colSpan={header.colSpan}
                      className="border border-gray-300 py-1 px-[2px] text-center font-bold text-xs"
                      style={{
                        width:
                          header.getSize() > 0 ? header.getSize() : undefined,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white text-center text-xs text-gray-700">
              {table.getRowModel().rows.map((row, rowIndex) => {
                const rowId = rowIndex;
                const lob = row.original.LOB;
                // Expansion is only possible if districts are selected AND it's not the total row
                const isExpandable =
                  selectedDistricts.length > 0 && // Enable expansion only if districts are selected
                  lob !== "Total" &&
                  Object.keys(productLineMap).includes(lob);

                return (
                  <React.Fragment key={`row-fragment-${rowIndex}`}>
                    <tr
                      key={`row-${rowIndex}`}
                      className={`${
                        row.original.LOB === "Total"
                          ? "bg-[#D3DDE6]  font-bold"
                          : rowIndex % 2 !== 0
                          ? ""
                          : "bg-[#EEF0F5]"
                      }`}
                    >
                      {row.getVisibleCells().map((cell, cellIndex) => {
                        const isLobCell = cellIndex === 0 && isExpandable;

                        // Determine cellClass based on your conditions
                        let cellClass = "";

                        if (
                          [0, 2, 4].includes(rowIndex) &&
                          cellIndex >= 1 &&
                          cellIndex <= 11
                        ) {
                          cellClass = "bg-[#EEF0F5]";
                        }

                        if (cellIndex === 0) {
                          cellClass = "bg-[#D3DDE6]";
                        }

                        if (row.original.LOB === "Total") {
                          cellClass = "bg-[#D3DDE6] font-bold";
                        }

                        return (
                          <td
                            key={`cell-${cell.id}`}
                            className={`border border-[#B9B9B9] py-[6px] text-xs ${cellClass} ${
                              isLobCell ? "cursor-pointer" : ""
                            }`}
                            onClick={
                              isLobCell
                                ? () => handleRowExpand(rowId, lob)
                                : undefined
                            }
                          >
                            {isLobCell ? (
                              <div className="flex items-center justify-center">
                                <span>{lob}</span>
                                {expandedRows[rowId] === lob ? (
                                  <IoIosArrowDown className="ml-2" />
                                ) : (
                                  <IoIosArrowForward className="ml-2" />
                                )}
                              </div>
                            ) : (
                              flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {/* Only render ExpandedRowRenderer if the row is expandable and currently expanded */}
                    {isExpandable && expandedRows[rowId] === lob && (
                      <ExpandedRowRenderer
                        rowId={rowId}
                        lob={lob}
                        columns={expandedColumns}
                        selectedDistricts={selectedDistricts}
                        filterData={filterData}
                        columnsCount={table.getFlatHeaders().length}
                        processDistrictData={processDistrictData}
                        parentLeafColumnWidths={parentLeafColumnWidths}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Separate component for expanded rows to properly use hooks
const ExpandedRowRenderer = ({
  rowId,
  lob,
  columns,
  selectedDistricts,
  filterData,
  columnsCount,
  processDistrictData,
  parentLeafColumnWidths,
}) => {
  const queryParams = useMemo(() => {
    const productLineName = productLineMap[lob] || lob;

    return {
      ...filterData,
      District: selectedDistricts,
      Product_Line: [productLineName],
    };
  }, [filterData, selectedDistricts, lob]);

  const {
    data: districtData = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "msVahanDistrictsData",
      JSON.stringify(selectedDistricts),
      lob,
      JSON.stringify(filterData),
    ],
    queryFn: () => districtsDataApi(queryParams),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    retry: 2,
    // Only enable this query if districts are selected in the parent component
    enabled: selectedDistricts.length > 0,
  });

  if (isLoading) {
    return (
      <tr>
        <td colSpan={columnsCount} className="p-2">
          <div className="flex justify-center items-center py-4">
            <p>Loading expanded data...</p>
            <Loader size="sm" />
          </div>
        </td>
      </tr>
    );
  }

  if (error) {
    return (
      <tr>
        <td colSpan={columnsCount} className="p-2">
          <div className="text-center text-red-500 py-4">
            Error loading expanded data: {error.message}
          </div>
        </td>
      </tr>
    );
  }

  const processedData = processDistrictData(districtData, lob);

  if (processedData.length === 0) {
    return (
      <tr>
        <td colSpan={columnsCount} className="p-2">
          <div className="text-center py-4">
            No detailed data available for {lob} in selected districts.
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td colSpan={columnsCount} className="p-0 border-none">
        <ExpandedRowContent
          data={processedData}
          columns={columns}
          parentLeafColumnWidths={parentLeafColumnWidths}
        />
      </td>
    </tr>
  );
};

export default MsVahanTiv2;
